import { IS_CLOUD } from '@qality/common-config/src/featureFlags';
import { JiraPermissions } from '@qality/common-model';
import { StorageOfUniqueServices } from '@qality/common-service';
import { Utils } from '@qality/common-utils';

export const PERMISSIONS_EDIT_ISSUES = 'EDIT_ISSUES';
export const CREATE_ATTACHMENTS = 'CREATE_ATTACHMENTS';
export const DELETE_ALL_ATTACHMENTS = 'DELETE_ALL_ATTACHMENTS';
export const DELETE_OWN_ATTACHMENTS = 'DELETE_OWN_ATTACHMENTS';
export const PERMISSIONS_ADMINISTER = 'ADMINISTER';

const CONTEXT_PERMISSIONS = [
  PERMISSIONS_EDIT_ISSUES,
  CREATE_ATTACHMENTS,
  DELETE_ALL_ATTACHMENTS,
  DELETE_OWN_ATTACHMENTS,
];

export default {
  hasPermissions(
    issueKey: string,
    permissions: Array<string> = CONTEXT_PERMISSIONS
  ): Promise<JiraPermissions> {
    return StorageOfUniqueServices.get('API')
      .request(
        `/rest/api/2/mypermissions?permissions=${permissions.join()}&issueKey=${issueKey}`
      )
      .then((response) => {
        const body = JSON.parse(response.body);
        const results = {};
        permissions.forEach((permission) => {
          results[permission] = body.permissions[permission].havePermission;
        });
        return results;
      });
  },

  hasPermissionsByIssueId(
    issueId: string | number,
    permissions: Array<string> = CONTEXT_PERMISSIONS
  ): Promise<JiraPermissions> {
    return StorageOfUniqueServices.get('API')
      .request(
        `/rest/api/2/mypermissions?permissions=${permissions.join()}&issueId=${issueId}`
      )
      .then((response) => {
        const body = JSON.parse(response.body);
        const results = {};
        permissions.forEach((permission) => {
          results[permission] = body.permissions[permission].havePermission;
        });
        return results;
      });
  },

  hasEditIssuePermission(
    value: string,
    property = 'issueKey'
  ): Promise<boolean> {
    return StorageOfUniqueServices.get('API')
      .request(
        `/rest/api/2/mypermissions?${property}=${value}&permissions=${PERMISSIONS_EDIT_ISSUES}`
      )
      .then((response) => {
        const body = JSON.parse(response.body);
        return body.permissions[PERMISSIONS_EDIT_ISSUES].havePermission;
      });
  },

  hasProjectPermissions(
    value: string | number,
    permissions: Array<string>
  ): Promise<JiraPermissions> {
    return StorageOfUniqueServices.get('API')
      .noRetry(
        `/rest/api/2/mypermissions?projectId=${value}&permissions=${permissions.join()}`
      )
      .then((response) => {
        const body = JSON.parse(response.body);
        const results = {};
        permissions.forEach((permission) => {
          results[permission] = body.permissions[permission].havePermission;
        });
        return results;
      });
  },

  isJiraAdminUser(): Promise<boolean> {
    return StorageOfUniqueServices.get('API')
      .request(
        `/rest/api/2/mypermissions?permissions=${PERMISSIONS_ADMINISTER}`
      )
      .then((response) => {
        const body = JSON.parse(response.body);
        return body.permissions[PERMISSIONS_ADMINISTER].havePermission;
      });
  },

  async isProjectAdminInGivenProject(projectKey: string): Promise<boolean> {
    const [projects, isJiraAdminUser] = await Promise.all([
      getProjectsWhereUserIsAdmin(),
      this.isJiraAdminUser(),
    ]);
    return (
      isJiraAdminUser ||
      (!Utils.isNilOrEmpty(projects) &&
        projects.some((project) => project.key === projectKey))
    );
  },

  async isProjectAdminInAnyProject(): Promise<boolean> {
    const [projects, isJiraAdminUser] = await Promise.all([
      getProjectsWhereUserIsAdmin(),
      this.isJiraAdminUser(),
    ]);
    return isJiraAdminUser || !Utils.isNilOrEmpty(projects);
  },

  async getEditPermissionForEachProject(
    projectIds: Array<string>
  ): Promise<{ [key: number]: boolean }> {
    const promises = [];
    projectIds.forEach((projectId) => {
      promises.push(
        this.hasProjectPermissions(projectId, [PERMISSIONS_EDIT_ISSUES]).then(
          (projectPermissions) => {
            return {
              projectId,
              permissions: projectPermissions,
            };
          }
        )
      );
    });

    const editPermissionMap = {};
    const permissions = await Promise.all(promises);

    permissions.forEach((permission) => {
      editPermissionMap[permission.projectId] =
        permission.permissions[PERMISSIONS_EDIT_ISSUES];
    });
    return editPermissionMap;
  },
};

async function getProjectsWhereUserIsAdmin(): Promise<
  Array<{ id: number; key: string }>
> {
  return IS_CLOUD
    ? getProjectsWhereUserIsAdminCloud()
    : getProjectsWhereUserIsAdminServer();
}

async function getProjectsWhereUserIsAdminCloud(): Promise<
  Array<{ id: number; key: string }>
> {
  const response = await StorageOfUniqueServices.get('API').request({
    url: '/rest/api/2/permissions/project',
    type: 'POST',
    contentType: 'application/json',
    data: JSON.stringify({
      permissions: ['ADMINISTER_PROJECTS'],
    }),
  });
  return JSON.parse(response.body).projects;
}

async function getProjectsWhereUserIsAdminServer(): Promise<
  Array<{
    id: number;
    key: string;
  }>
> {
  return StorageOfUniqueServices.get(
    'AdministeredProjectService'
  ).getAllAdministeredProjects({ includeSettingsProperty: true });
}
